import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useTheme } from 'styled-components/native';
import { RFValue } from 'react-native-responsive-fontsize';
import { ActivityIndicator, Animated, Dimensions, Easing, FlatList, TouchableWithoutFeedback, View } from 'react-native';
import { AppEventsLogger } from 'react-native-fbsdk-next';
import { Button } from '../../components/Interface/Forms/Buttons/Button';
import { Header } from '../../components/Interface/Headers/Header';
import { version } from '../../../package.json';
import analytics from '@react-native-firebase/analytics';
import remoteConfig from '@react-native-firebase/remote-config';
import {trackEvent} from '../../lib/analytics';
import {
  Container,
  SectionTitle,
  Section,
  SectionHeader,
  List,
  Price,
  PackageSelect,
  PackageSelectValue,
  PackageSelectValueDescription,
  QuantityContainer,
  QuantityButton,
  QuantityIcon,
  Quantity,
  Footer,
  FuelList,
  FuelItemContainer,
  FuelItemWrapper,
  FuelItem,
  FuelFlag,
  FuelData,
  FuelName,
  EtanolBalloon,
  EtanolBalloonText,
  EtanolBalloonArrow,
  EtanolBalloonMascote,
  SectionTitle2,
  QuantityColumn,
  AlertContainer,
  FuelNameContainer,
  EncherTanqueContainer,
  EncherTanqueText,
  PumpIconContainer,
  PumpIcon,
} from './styles';
import { StepList } from '../../components/Interface/List/StepList';
import { useAuth } from '../../context/auth';
import { StationDTO } from '../../dtos/StationDTO';
import { api } from '../../services/api';
import { rastrear } from '../../services/rastrear';
import { RootStackParamList } from '../../dtos/RoutesDTO';
import formatFuelData from '../../utils/formatFuelData';
import { ProductDTO } from '../../dtos/ProductDTO';
import AnimatedTitle, { AnimatedTitleHandle } from './components/AnimatedTitle';
import { Step } from '../../components/Interface/Step';
import { getPersistentDeviceId } from '../../utils/deviceId';
import { GasStationLocations } from '../../components/shared/GasStationLocations';
import { EncherTanqueBottomSheet } from '../../components/shared/EncherTanqueBottomSheet';

import mascoteMegafone from '../../assets/images/mascote/mascote-megafone.png';
import mascoteBico  from '../../assets/images/mascote/mascote-bico.png';
import mascoteMegafoneR from '../../assets/images/mascote/mascote-megafoneR.png';
import mascoteBicoR from '../../assets/images/mascote/mascote-bicoR.png';

const TANQUE_CHEIO_ITEM = { volume: 'tanque_cheio' };

const DEFAULT_FUEL: ProductDTO = {
  id: '',
  name: '',
  ordem: '',
  description: '',
  regularPrice: '',
  price: 'R$0,00',
  valorComDesconto: '',
  imageUrl: '',
  region: '',
  alerta: '',
  unidade: 'Litros',
  volume: [
    { volume: 3 },
    { volume: 5 },
    { volume: 10 },
    { volume: 15 },
    { volume: 20 },
    { volume: 25 },
    { volume: 30 },
    { volume: 35 },
    { volume: 40 },
    { volume: 45 },
    { volume: 50 },
  ],
  updated_at: '',
  cidade: '',
  localidade: '',
  categoria: '',
};

const ETANOL_BALLOON_MESSAGES = [
  {
    text: 'Hoje vale ir\n de Etanol',
    mascote: mascoteMegafone,
    mascoteR: mascoteMegafoneR,
    mascoteSize: 32,
  },
  {
    text: 'Mais econômico\ne sustentável',
    mascote: mascoteBico,
    mascoteR: mascoteBicoR,
    mascoteSize: 32,
  },
];
const ETANOL_BALLOON_SWITCH_INTERVAL_MS = 5000;
const ETANOL_BALLOON_FADE_DURATION_MS = 600;
const ETANOL_PARITY_THRESHOLD = 0.7;

function EtanolBalloonRotator({mascoteOnLeft}: {mascoteOnLeft: boolean}) {
  const [messageIndex, setMessageIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const interval = setInterval(() => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: ETANOL_BALLOON_FADE_DURATION_MS,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }).start(() => {
        setMessageIndex(prev => (prev + 1) % ETANOL_BALLOON_MESSAGES.length);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: ETANOL_BALLOON_FADE_DURATION_MS,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }).start();
      });
    }, ETANOL_BALLOON_SWITCH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fadeAnim]);

const currentMessage = ETANOL_BALLOON_MESSAGES[messageIndex];
  const mascoteSource = mascoteOnLeft ? currentMessage.mascoteR : currentMessage.mascote;

  return (
    <Animated.View
      style={{
        opacity: fadeAnim,
        flexDirection: mascoteOnLeft ? 'row-reverse' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <EtanolBalloonText>{currentMessage.text}</EtanolBalloonText>
      <EtanolBalloonMascote
        source={mascoteSource}
        resizeMode="contain"
        size={currentMessage.mascoteSize}
        onLeft={mascoteOnLeft}
      />
    </Animated.View>
  );
}

export function Fuel() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const { isLogged, openSign } = useAuth();

  const fuelTitleRef = useRef<AnimatedTitleHandle>(null);
  const quantityTitleRef = useRef<AnimatedTitleHandle>(null);
  const flatListRef = useRef<FlatList>(null);
  const scrollOffsetRef = useRef(0);
  const quantityTitleRef2 = useRef<View>(null);
  const couponTitleRef2 = useRef<View>(null);
  const isQuantityFromTanqueRef = useRef(false);

  const { localidade, posto, coupon } =
    route.params as RootStackParamList['Fuel'];

  const [isLoading, setIsLoading] = useState(true);
  const isSP = localidade?.estado?.toUpperCase() === 'SP';

  //switch remoto: permite ligar/desligar a feature do Etanol via
  // Firebase Remote Config (chave "exibe_destaque_etanol_sp")
  const [etanolFeatureAtiva, setEtanolFeatureAtiva] = useState(false);

  useEffect(() => {
    remoteConfig()
      .setDefaults({ exibe_destaque_etanol_sp: 'true' })
      .then(() => remoteConfig().setConfigSettings({ minimumFetchIntervalMillis: 0 }))
      .then(() => remoteConfig().fetchAndActivate())
      .then(() => {
        const ativa = remoteConfig().getValue('exibe_destaque_etanol_sp').asBoolean();
        setEtanolFeatureAtiva(ativa);
      })
      .catch(() => {
      });
  }, []);

  const [showTanqueCheio, setShowTanqueCheio] = useState(false);

  const LOADING_MESSAGES = [
    'Carregando produtos...',
    'Preparando listagem...',
    'Buscando preços...',
    'Verificando disponibilidade...',
    'Quase pronto...',
  ];

  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const loadingFadeAnim = useRef(new Animated.Value(1)).current;
  const etanolPulseAnim = useRef(new Animated.Value(1)).current;

  function triggerEtanolPulse() {
    Animated.sequence([
      Animated.timing(etanolPulseAnim, {
        toValue: 1.06,
        duration: 120,
        useNativeDriver: true,
      }),
      Animated.timing(etanolPulseAnim, {
        toValue: 1,
        duration: 120,
        useNativeDriver: true,
      }),
    ]).start();
  }

  useFocusEffect(
    useCallback(() => {
      rastrear({status_tela: 'detalhesPostos', cidade: localidade.cidade});
    }, [localidade.cidade]),
  );

  useEffect(() => {
    if (!isLoading) {return;}
    const interval = setInterval(() => {
      Animated.timing(loadingFadeAnim, { toValue: 0, duration: 400, useNativeDriver: true }).start(() => {
        setLoadingMsgIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
        Animated.timing(loadingFadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
      });
    }, 1800);
    return () => clearInterval(interval);
  }, [isLoading]);

  async function fetchValidProducts(produtos: ProductDTO[]) {
    try {
      const { data } = await api.get('/produtos_validos', {
        params: {
          codigo_desconto: coupon?.codigo,
          nome_posto: posto.name,
        },
      });

      const couponFuels = [] as ProductDTO[];

      data.produtos.map((produto: any) => {
        const findCurrentFuel = produtos.find(
          item => item.name === produto.descricao,
        );
        if (findCurrentFuel) {
          const fuelFormatado: ProductDTO = {
            ...findCurrentFuel,
            volume: handleAvailableVolume(
              findCurrentFuel.volume,
              data.litros_restantes,
            ),
          };
          couponFuels.push(fuelFormatado);
        }
      });

      setFuelsAvailable(couponFuels.reverse());
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchTanqueCheio() {
    try {
      const { data } = await api.get('/tanqueCheio');
      setShowTanqueCheio(data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchTanqueCheio();
  }, []);

  useEffect(() => {
    async function fetchFuels() {
      const deviceId = await getPersistentDeviceId();
      try {
        await api
          .get('/fuels', {
            params: {
              tipo: 'C',
              versao: version,
              deviceId,
              regiao: localidade.region,
              codigo_desconto: coupon?.codigo,
            },
          })
          .then(({ data }) => {
            if (coupon?.codigo) {
              fetchValidProducts(data);
            } else {
              setFuelsAvailable(data);
              setIsLoading(false);
            }
          })
          .catch(() => {
            setIsLoading(false);
          });
      } catch (error) {
        console.log(error);
      }
    }

    fetchFuels();
  }, []);

  const parsePrice = (price: string) =>
    parseFloat(price.replace('R$', '').replace(',', '.'));

  const [fuelsAvailable, setFuelsAvailable] = useState<ProductDTO[]>(
    [] as ProductDTO[],
  );

  // PROTÓTIPO: destaque/balão de "vale a pena ir de Etanol" quando o preço do
  // etanol estiver abaixo da paridade com a gasolina de referência (regra dos 70%).
  const gasolinaReferenciaPrice = useMemo(() => {
    const gasolinaComum = fuelsAvailable.find(
      fuel => fuel.name.toLowerCase().includes('gasolina comum'),
    );
    if (gasolinaComum) {
      return parsePrice(gasolinaComum.price);
    }

    const gasolinaAditivada = fuelsAvailable.find(
      fuel => fuel.name.toLowerCase().includes('gasolina aditivada'),
    );
    return gasolinaAditivada ? parsePrice(gasolinaAditivada.price) : null;
  }, [fuelsAvailable]);

  const hasEtanolComum = useMemo(() => {
    return fuelsAvailable.some(fuel => {
      const nomeLower = fuel.name.toLowerCase();
      return nomeLower.includes('etanol') && !nomeLower.includes('aditiv');
    });
  }, [fuelsAvailable]);

  function isEtanolVantajoso(fuel: ProductDTO) {
    if (!etanolFeatureAtiva) {
      return false;
    }

    const nomeLower = fuel.name.toLowerCase();

    if (!gasolinaReferenciaPrice || !nomeLower.includes('etanol')) {
      return false;
    }

    // Só exclui o Aditivado quando o Etanol comum TAMBÉM está disponível —
    // nesse caso, o comum sempre prevalece. Se só existir o Aditivado,
    // ele pode receber o destaque normalmente.
    const isAditivado = nomeLower.includes('aditivado') || nomeLower.includes('aditiv');
    if (isAditivado && hasEtanolComum) {
      return false;
    }

    const etanolPrice = parsePrice(fuel.price);
    if (!etanolPrice) {
      return false;
    }
    return etanolPrice / gasolinaReferenciaPrice < ETANOL_PARITY_THRESHOLD;
  }

  // Ordena: combustíveis normais primeiro; entre os etanóis, o vantajoso
  // (o que exibe o balão/destaque) sempre fica por último de todos, evitando
  // que o balão colida visualmente com o card da linha seguinte.
  const sortedFuelsAvailable = useMemo(() => {
    return [...fuelsAvailable].sort((a, b) => {
      const aIsEtanol = a.name.toLowerCase().includes('etanol');
      const bIsEtanol = b.name.toLowerCase().includes('etanol');

      if (aIsEtanol && !bIsEtanol) {return 1;}
      if (!aIsEtanol && bIsEtanol) {return -1;}

      if (aIsEtanol && bIsEtanol) {
        const aVantajoso = isEtanolVantajoso(a);
        const bVantajoso = isEtanolVantajoso(b);
        if (aVantajoso && !bVantajoso) {return 1;}
        if (!aVantajoso && bVantajoso) {return -1;}
      }

      return 0;
    });
  }, [fuelsAvailable, gasolinaReferenciaPrice, hasEtanolComum, etanolFeatureAtiva]);

const showMascoteOnLeft = fuelsAvailable.length === 3;

  const [currentFuel, setCurrentFuel] = useState<ProductDTO>(DEFAULT_FUEL);

  const fuelPrice =
    currentFuel &&
    parseFloat(currentFuel.price.replace('R$', '').replace(',', '.'));

  const [amount, setAmount] = useState(fuelPrice * 3);

  const [quantity, setQuantity] = useState(0);
  const [multiplier, setMultiplier] = useState(1);
  const [stationsByProductId, setStationsByProductId] = useState<StationDTO[]>(
    [] as StationDTO[],
  );

  const [tanqueBottomSheetVisible, setTanqueBottomSheetVisible] = useState(false);

  const handleOpenTanqueModal = () => {
    if (!currentFuel.id) {
      fuelTitleRef.current?.shake();
      return;
    }
    const price = parseFloat(currentFuel.price.replace('R$', '').replace(',', '.'));
    if (!price || isNaN(price)) {
      return;
    }
    setTanqueBottomSheetVisible(true);
  };

  const buttonIsActive = useMemo(
    () => quantity > 0 && !!currentFuel.id,
    [quantity, currentFuel],
  );

  const scrollToQuantity = useCallback(() => {
    setTimeout(() => {
      quantityTitleRef2.current?.measureInWindow((_x, y) => {
        const targetOffset = scrollOffsetRef.current + y - RFValue(120);
        flatListRef.current?.scrollToOffset({
          offset: Math.max(0, targetOffset),
          animated: true,
        });
      });
    }, 150);
  }, []);

  const scrollToCoupons = useCallback(() => {
    setTimeout(() => {
      couponTitleRef2.current?.measureInWindow((_x, y) => {
        const targetOffset = scrollOffsetRef.current + y - RFValue(80);
        flatListRef.current?.scrollToOffset({
          offset: Math.max(0, targetOffset),
          animated: true,
        });
      });
    }, 150);
  }, []);

  const handleAmount = (value: number) => {
    isQuantityFromTanqueRef.current = false;
    let newValue = value;
    if (value === quantity) {
      newValue = 0;
    }

    setQuantity(newValue);
    const math = newValue * fuelPrice;
    setAmount(math);

    if (newValue > 0 && !coupon) {
      scrollToCoupons();
    }
  };

  const handleConfirmQuantityFromTanque = useCallback(
    (liters: number) => {
      isQuantityFromTanqueRef.current = true;
      setQuantity(liters);
      setAmount(liters * fuelPrice);
      if (liters > 0 && !coupon) {
        scrollToCoupons();
      }
    },
    [fuelPrice, coupon, scrollToCoupons],
  );

  const onSelectFuel = (fuel: ProductDTO) => {
    isQuantityFromTanqueRef.current = false;
    if (fuel.id === currentFuel.id) {
      setCurrentFuel(DEFAULT_FUEL);
    } else {
      setCurrentFuel(fuel);
      if (isEtanolVantajoso(fuel)) {
        triggerEtanolPulse();
      }
      scrollToQuantity();
    }
  };

  const formatData = (data: any, numColumns: number) => {
    const numberOfFullRows = Math.floor(data.length / numColumns);

    let numberOfElementsLastRow = data.length - numberOfFullRows * numColumns;
    while (
      numberOfElementsLastRow !== numColumns &&
      numberOfElementsLastRow !== 0
    ) {
      data.push({ key: `blank-${numberOfElementsLastRow}`, empty: true });
      numberOfElementsLastRow++;
    }

    return data;
  };

  const renderFuelName = (name: string) => {
    const cuttedName = name.split(' ');
    if (cuttedName.length > 1) {
      return (
        <FuelName>
          {cuttedName.map((n, i) => i === cuttedName.length - 1 ? n : `${n}\n`)}
        </FuelName>
      );
    }

    return <FuelName>{cuttedName[0]}</FuelName>;
  };

  function handlePayment(overrides?: { quantity: number; amount: number }) {
    const finalQuantity = overrides?.quantity ?? quantity;
    const finalAmount = overrides?.amount ?? amount;

    if (!currentFuel.id) {
      return fuelTitleRef.current?.shake();
    }
    if (finalQuantity === 0) {
      return quantityTitleRef.current?.shake();
    }

    if (isLogged) {
      analytics().logEvent('seleciona_produto_e_litragem', {
        id: 101,
        VALUE: finalQuantity * multiplier,
        currency: 'BRL',
        items: [
          {
            item_id: currentFuel.id,
            item_name: currentFuel.name,
            price: finalAmount,
            quantity: finalQuantity,
          },
        ],
      });

      AppEventsLogger.logEvent('seleciona_combustivel', {
        nome: currentFuel.name,
        litros: finalQuantity,
        multiplicador: multiplier,
        preco: finalAmount,
      });

      const fluxoPagamento = 'Payment';

      navigation.navigate(fluxoPagamento, {
        pedidos: {
          fuelId: currentFuel.id,
          imageUrl: currentFuel.imageUrl,
          liters: finalQuantity,
          name: currentFuel.name,
          quantity: multiplier,
          value: finalAmount * multiplier,
          unidade: currentFuel.unidade,
        },
        gnv: currentFuel.name.toLowerCase() === 'gnv',
        localidade,
        coupon,
        posto,
        stationsByProductId,
      });
    } else {
      openSign('loginNoFluxo');
    }
  }

  const numColumns = 5;

  const volumeData = useMemo(() => {
    const base = [...currentFuel.volume] as { volume: number | string }[];
    if (showTanqueCheio) {
      base.push(TANQUE_CHEIO_ITEM);
    }
    return base;
  }, [currentFuel.volume, showTanqueCheio]);

  function handleAddQuantity() {
    setMultiplier(previous => {
      const newValue = previous + 1;
      trackEvent('view_item', {
        currency: 'BRL',
        value: fuelPrice,
        items: [
          {
            item_id: currentFuel.id,
            item_name: currentFuel.name,
            quantity: quantity * newValue,
          },
        ],
      });
      return newValue;
    });
  }

  function handleRemoveQuantity() {
    setMultiplier(previous => {
      const newValue = previous - 1;
      trackEvent('view_item', {
        currency: 'BRL',
        value: fuelPrice,
        items: [
          {
            item_id: currentFuel.id,
            item_name: currentFuel.name,
            quantity: quantity * newValue,
          },
        ],
      });
      return newValue;
    });
  }

  useEffect(() => {
    async function fetchStationsByProductId() {
      await api
        .get('/listaPostosCompra', {
          params: {
            id_produto: currentFuel.id,
            codigo_desconto: coupon?.codigo,
            regiao: currentFuel.region,
          },
        })
        .then(res => {
          setStationsByProductId(res.data);
        })
        .catch(error => {
          console.log(error);
        });
    }

    if (currentFuel) {
      fetchStationsByProductId();
    }
  }, [currentFuel]);

  useEffect(() => {
    if (quantity === 0 || !currentFuel.id) {
      return;
    }
    if (isQuantityFromTanqueRef.current) {
      isQuantityFromTanqueRef.current = false;
      return;
    }
    const price = parsePrice(currentFuel.price);
    const hasSameVolume = currentFuel.volume.find(v => v.volume === quantity);
    if (hasSameVolume) {
      setAmount(price * quantity);
      return;
    }
    const firstVolume = currentFuel.volume.find(f => f?.volume);
    if (firstVolume?.volume) {
      setAmount(price * firstVolume.volume);
      setQuantity(firstVolume.volume);
      return;
    }
  }, [quantity, currentFuel]);

  function handleAvailableVolume(
    volume: { volume: number }[],
    maxLiters: number,
  ) {
    if (maxLiters) {
      return volume.filter(v => Number(v.volume) <= maxLiters);
    }
    return volume;
  }

  return (
    <View style={{ flex: 1 }}>

      {isLoading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', gap: 16 }}>
          <ActivityIndicator color={theme.colors.primary} size="large" />
          <Animated.Text
            style={{
              opacity: loadingFadeAnim,
              color: theme.colors.text,
              fontSize: RFValue(13),
              fontFamily: theme.fonts.medium,
              marginTop: 8,
            }}>
            {LOADING_MESSAGES[loadingMsgIndex]}
          </Animated.Text>
        </View>
      ) : (
        currentFuel && (
          <SafeAreaView style={{ flex: 1 }} edges={['bottom']}>
            <Container>
              <Header icon="chevron-left" />
              <FlatList
                ref={flatListRef}
                onScroll={(e) => { scrollOffsetRef.current = e.nativeEvent.contentOffset.y; }}
                scrollEventThrottle={16}
                ListHeaderComponent={
                  <>
                    <List style={{ marginHorizontal: 24 }}>
                      <StepList
                        containerStyle={{
                          backgroundColor: theme.colors.orange_opacity,
                          borderWidth: 1,
                          borderStyle: 'solid',
                          borderColor: theme.colors.orange,
                        }}
                        textStyle={{
                          color: theme.colors.orange_dark,
                          fontFamily: theme.fonts.medium,
                        }}
                        text="Antes de realizar uma compra, verifique se a quantidade
                    escolhida irá caber no seu veículo."
                      />
                    </List>
                    <SectionHeader>
                      <AnimatedTitle ref={fuelTitleRef}>
                        Qual combustível?
                      </AnimatedTitle>
                      <SectionTitle2>*Preço por litro</SectionTitle2>
                    </SectionHeader>
                    <FuelList>
                      {fuelsAvailable.length >= 1 &&
                        sortedFuelsAvailable.map((fuel, index) => {
                          const { flag, name, bgColor } = formatFuelData(
                            fuel.name,
                          );
                          const etanolVantajoso = isEtanolVantajoso(fuel) && isSP;

                          return index === 0 ? (
                            <Step
                              key={fuel.id}
                              contextName="welcome"
                              order={8}
                              text="Selecione o combustível."
                              onPress={() => onSelectFuel(fuel)}>
                              <FuelItemWrapper>
                                <Animated.View style={etanolVantajoso ? {transform: [{scale: etanolPulseAnim}]} : undefined}>
                                  <FuelItemContainer
                                    active={fuel.id === currentFuel.id}
                                    highlight={etanolVantajoso}
                                    style={{
                                      shadowColor: theme.colors.text_dark,
                                      shadowOffset: { width: 0, height: 4 },
                                      shadowOpacity: 0.17,
                                      shadowRadius: 12,
                                      elevation: 0,
                                    }}
                                    onPress={() => {
                                      onSelectFuel(fuel);
                                    }}>
                                    <FuelItem bgColor={bgColor}>
                                      {flag ? <FuelFlag>{flag}</FuelFlag> : null}
                                      <FuelData>
                                        <FuelNameContainer>
                                          {renderFuelName(name)}
                                        </FuelNameContainer>
                                        <Price>{fuel.valorComDesconto}</Price>
                                      </FuelData>
                                    </FuelItem>
                                  </FuelItemContainer>
                                </Animated.View>
                                {etanolVantajoso && (
                                  <EtanolBalloon>
                                    <EtanolBalloonArrow />
                                    <EtanolBalloonRotator mascoteOnLeft={showMascoteOnLeft} />
                                  </EtanolBalloon>
                                )}
                              </FuelItemWrapper>
                            </Step>
                          ) : (
                            <FuelItemWrapper key={fuel.id}>
                              <Animated.View style={etanolVantajoso ? {transform: [{scale: etanolPulseAnim}]} : undefined}>
                                <FuelItemContainer
                                  active={fuel.id === currentFuel.id}
                                  highlight={etanolVantajoso}
                                  style={{
                                    shadowColor: theme.colors.text_dark,
                                    shadowOffset: { width: 0, height: 4 },
                                    shadowOpacity: 0.17,
                                    shadowRadius: 12,
                                    elevation: 0,
                                  }}
                                  onPress={() => {
                                    onSelectFuel(fuel);
                                  }}>
                                  <FuelItem bgColor={bgColor}>
                                    {flag ? <FuelFlag>{flag}</FuelFlag> : null}
                                    <FuelData>
                                      {renderFuelName(name)}
                                      <Price>{fuel.valorComDesconto}</Price>
                                    </FuelData>
                                  </FuelItem>
                                </FuelItemContainer>
                              </Animated.View>
                              {etanolVantajoso && (
                                <EtanolBalloon>
                                  <EtanolBalloonArrow />
                                  <EtanolBalloonRotator mascoteOnLeft={showMascoteOnLeft} />
                                </EtanolBalloon>
                              )}
                            </FuelItemWrapper>
                          );
                        })}
                    </FuelList>

                    <View ref={quantityTitleRef2}>
                      <SectionHeader>
                        <AnimatedTitle ref={quantityTitleRef}>
                          Quantos {currentFuel.unidade}?
                        </AnimatedTitle>
                      </SectionHeader>
                    </View>
                  </>
                }
                data={formatData([...volumeData], numColumns)}
                contentContainerStyle={{
                  paddingBottom: RFValue(20),
                }}
                keyExtractor={item => item.volume}
                showsVerticalScrollIndicator={false}
                numColumns={numColumns}
                style={{
                  paddingTop: RFValue(6),
                }}
                columnWrapperStyle={{
                  paddingHorizontal: 16,
                }}
                renderItem={({ item, index }) => {

                  if (item.empty === true) {
                    return (
                      <PackageSelect
                        disabled
                        active={false}
                        style={{ borderColor: 'transparent', margin: 6 }}
                      />
                    );
                  }

                  if (item.volume === 'tanque_cheio') {
                    return (
                      <TouchableWithoutFeedback onPress={handleOpenTanqueModal}>
                        <EncherTanqueContainer colors={theme.colors.red_gradient} style={{ borderColor: 'transparent', margin: 6 }}>
                            <PumpIconContainer>
                              <PumpIcon name="gas-pump" />
                            </PumpIconContainer>
                            <EncherTanqueText>Tanque{'\n'}Cheio</EncherTanqueText>
                        </EncherTanqueContainer>
                      </TouchableWithoutFeedback>
                    );
                  }

                  return index === 0 ? (
                    <Step
                      order={9}
                      contextName="welcome"
                      text="Selecione a quantidade em litros."
                      customStyle={{
                        flex: 1,
                        margin: 6,
                        height:
                          (Dimensions.get('window').width - 96) / numColumns,
                      }}
                      onPress={() => handleAmount(parseFloat(item.volume))}>
                      <PackageSelect
                        activeOpacity={1}
                        active={quantity === parseFloat(item.volume)}
                        onPress={() => handleAmount(parseFloat(item.volume))}>
                        <PackageSelectValue>{item.volume}</PackageSelectValue>
                        <PackageSelectValueDescription>
                          {item.volume === '1'
                            ? currentFuel.unidade.endsWith('s')
                              ? currentFuel.unidade.slice(0, -1)
                              : currentFuel.unidade
                            : currentFuel.unidade}
                        </PackageSelectValueDescription>
                      </PackageSelect>
                    </Step>
                  ) : (
                    <PackageSelect
                      activeOpacity={1}
                      active={quantity === parseFloat(item.volume)}
                      onPress={() => handleAmount(parseFloat(item.volume))}
                      style={{
                        height:
                          (Dimensions.get('window').width - 96) / numColumns,
                        margin: 6,
                      }}>
                      <PackageSelectValue>{item.volume}</PackageSelectValue>
                      <PackageSelectValueDescription>
                        {item.volume === '1'
                          ? currentFuel.unidade.endsWith('s')
                            ? currentFuel.unidade.slice(0, -1)
                            : currentFuel.unidade
                          : currentFuel.unidade}
                      </PackageSelectValueDescription>
                    </PackageSelect>
                  );

                }}
                ListFooterComponent={
                  <>
                    {!coupon && (
                      <View ref={couponTitleRef2}>
                        <Section
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',

                            justifyContent: 'space-between',
                          }}>
                          <SectionTitle>Quantos Cupons?</SectionTitle>

                          <QuantityColumn>
                            <QuantityContainer>
                              <QuantityButton
                                activeOpacity={1}
                                disabled={multiplier <= 1}
                                style={{ opacity: multiplier <= 1 ? 0.4 : 1 }}
                                onPress={handleRemoveQuantity}>
                                <QuantityIcon name="minus" />
                              </QuantityButton>
                              <Quantity>{multiplier}</Quantity>
                              <QuantityButton
                                activeOpacity={1}
                                disabled={multiplier >= 5}
                                style={{ opacity: multiplier >= 5 ? 0.4 : 1 }}
                                onPress={handleAddQuantity}>
                                <QuantityIcon name="plus" />
                              </QuantityButton>
                            </QuantityContainer>
                          </QuantityColumn>
                        </Section>
                      </View>
                    )}

                    <Section>
                      <GasStationLocations
                        posto={posto}
                        stationsByProductId={stationsByProductId}
                        localidade={localidade}
                      />

                      {currentFuel.alerta &&
                        currentFuel.alerta.length === 0 && (
                          <AlertContainer>
                            <StepList
                              containerStyle={{
                                backgroundColor: theme.colors.red,
                                marginBottom: 0,
                                marginTop: RFValue(12),
                              }}
                              textStyle={{
                                color: theme.colors.shape,
                                fontFamily: theme.fonts.semibold,
                              }}
                              text={currentFuel.alerta}
                            />
                          </AlertContainer>
                        )}
                    </Section>

                    <Section>
                      <SectionTitle>Como funciona:</SectionTitle>

                      <List>
                        <StepList
                          number={1}
                          text={
                            'Escolha a quantidade em ' +
                            currentFuel.unidade.toLowerCase() +
                            '.'
                          }
                        />

                        <StepList number={2} text="Realize o pagamento." />
                        <StepList
                          number={3}
                          text="Dirija-se a um dos postos credenciados da região e categoria
                    escolhidas."
                        />
                        <StepList
                          number={4}
                          text="Apresente o cupom com QR Code para o frentista."
                        />
                      </List>
                    </Section>

                  </>
                }
              />
              <Footer>
                <Step
                  order={10}
                  contextName="welcome"
                  text="Finalize sua compra no checkout, vá até o posto credenciado e apresente o QR Code.">
                  <Button
                    title={'Continuar'}
                    color={
                      buttonIsActive ? undefined : theme.colors.silver_dark
                    }
                    onPress={handlePayment}
                    enabled
                  />
                </Step>
              </Footer>
            </Container>
            <EncherTanqueBottomSheet
              isOpen={tanqueBottomSheetVisible}
              onClose={() => setTanqueBottomSheetVisible(false)}
              fuelPrice={fuelPrice}
              currentFuel={currentFuel}
              onConfirmQuantity={handleConfirmQuantityFromTanque}
              onPayment={handlePayment}
            />
          </SafeAreaView>
        )
      )}
    </View>
  );
}
